import React, { useEffect, useRef, useState } from 'react';

const MusicNotesBackground = ({ className, opacity = 0.05, animate = true, noteCount = 100 }) => {
  const canvasRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);
  
  // Mobil cihaz tespiti
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768 || /iPhone|iPad|iPod|Android/i.test(navigator.userAgent));
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Mobilde animasyonu devre dışı bırak ve nota sayısını azalt
  const shouldAnimate = animate && !isMobile;
  const actualNoteCount = isMobile ? Math.min(30, noteCount) : Math.max(100, noteCount);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    
    // Müzik notaları
    const musicSymbols = [
      { symbol: '♪', scale: 1.0 },
      { symbol: '♫', scale: 1.2 },
      { symbol: '♬', scale: 1.1 },
      { symbol: '♩', scale: 0.9 },
      { symbol: '♭', scale: 0.8 },
      { symbol: '♮', scale: 0.85 },
      { symbol: '♯', scale: 0.85 },
      { symbol: '𝄞', scale: 1.5 }, // Sol anahtarı
    ];
    
    // Rastgele nota oluşturucu
    const createNote = () => {
      const symbol = musicSymbols[Math.floor(Math.random() * musicSymbols.length)];
      return {
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        symbol: symbol.symbol,
        size: (Math.random() * 25 + 15) * symbol.scale,
        speedX: Math.random() * 0.8 - 0.4, // -0.4 ile 0.4 arası
        speedY: Math.random() * 0.8 - 0.4, // -0.4 ile 0.4 arası
        opacity: Math.random() * 0.5 + 0.1, // 0.1 ile 0.6 arası
        rotationSpeed: (Math.random() * 2 - 1) * 0.01, // -0.01 ile 0.01 arası
        rotation: Math.random() * Math.PI * 2, // 0 ile 2π arası
        fadeDirection: Math.random() > 0.5 ? 1 : -1, // Opaklık artış yönü
        fadeSpeed: Math.random() * 0.001 + 0.0005, // Opaklık değişim hızı
        scale: Math.random() * 0.5 + 0.7, // Boyut çarpanı
        color: getRandomColor(0.7) // Rastgele renk
      };
    };
    
    // Rastgele renk oluşturucu
    function getRandomColor(saturation = 1) {
      // Müzikal renk paleti: mor, mavi ve indigo tonları
      const hue = Math.floor(Math.random() * 60) + 220; // 220-280 arası (mavi-mor spektrum)
      const lightness = Math.floor(Math.random() * 30) + 40; // 40-70% arası
      return `hsla(${hue}, ${saturation * 100}%, ${lightness}%, 1)`;
    }
    
    // Notaları oluştur - mobilde daha az nota
    const notes = Array(actualNoteCount).fill().map(() => createNote());
    
    // Notaları çiz ve canlandır
    const drawNotes = () => {
      if (!canvas) return;
      
      // Canvas'ı temizle
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Her bir notayı çiz
      notes.forEach(note => {
        ctx.save();
        ctx.globalAlpha = note.opacity * opacity; // Genel opaklık ayarını uygula
        ctx.font = `${note.size * note.scale}px serif`;
        ctx.fillStyle = note.color;
        
        // Notayı pozisyonuna ve rotasyonuna göre çiz
        ctx.translate(note.x, note.y);
        ctx.rotate(note.rotation);
        ctx.fillText(note.symbol, 0, 0);
        
        ctx.restore();
        
        if (shouldAnimate) {
          // Pozisyonu güncelle
          note.x += note.speedX;
          note.y += note.speedY;
          
          // Rotasyonu güncelle
          note.rotation += note.rotationSpeed;
          
          // Opaklığı değiştir (yavaşça solma ve yükselme efekti)
          note.opacity += note.fadeSpeed * note.fadeDirection;
          if (note.opacity > 0.6) {
            note.fadeDirection = -1; // Solmaya başla
          } else if (note.opacity < 0.1) {
            note.fadeDirection = 1; // Yükselmeye başla
          }
          
          // Ekran dışına çıkınca karşı taraftan geri getir
          if (note.x < -50) note.x = canvas.width + 50;
          if (note.x > canvas.width + 50) note.x = -50;
          if (note.y < -50) note.y = canvas.height + 50;
          if (note.y > canvas.height + 50) note.y = -50;
        }
      });
      
      // Animasyon devam etsin
      if (shouldAnimate) {
        requestAnimationFrame(drawNotes);
      }
    };
    
    // Canvas boyutlarını ayarla
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      
      // Ekran boyutu değiştiğinde nota pozisyonlarını yeniden dağıt
      notes.forEach(note => {
        note.x = Math.random() * canvas.width;
        note.y = Math.random() * canvas.height;
      });
      
      // Animasyon yoksa sadece bir kez çiz
      if (!shouldAnimate) {
        drawNotes();
      }
    };
    
    // Pencere boyutu değiştiğinde canvas'ı yeniden boyutlandır
    window.addEventListener('resize', resizeCanvas);
    resizeCanvas();
    
    // Animasyonu başlat
    let animationFrame;
    if (shouldAnimate) {
      animationFrame = requestAnimationFrame(drawNotes);
    } else {
      drawNotes();
    }
    
    // Temizleme
    return () => {
      window.removeEventListener('resize', resizeCanvas);
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
    };
  }, [opacity, shouldAnimate, actualNoteCount, isMobile]);
  
  return (
    <canvas 
      ref={canvasRef} 
      className={`absolute inset-0 pointer-events-none ${className || ''}`}
    />
  );
};

export default MusicNotesBackground;